// src/pages/NotificationsPage.tsx
//
// ★ PHASE 10 — Annex A story 50, "Email Notification for eCS New Entitlement
// Creation". PSD only. The rule/trigger/log sections below configure WHO
// would be notified and record that a notifiable event fired — unchanged
// since Phase 10.
//
// ★ TASK 7 of the 2026-08-15 notification-delivery wave adds the operator's
// own read of the OUTBOX Tasks 1-6 built (`app.notification_delivery` and its
// append-only attempt ledger): the delivery queue, the attempt ledger for a
// selected delivery, a Run outbox action and a Requeue action for an
// ABANDONED row. See `services/gateway/src/routes/notifications.ts`'s own
// header for why there is no timer — an operator has to ask.
//
// ⚠ THE STALE ALERT THIS TASK REMOVES. Until this task the banner below read a
// hardcoded "No email delivery mechanism exists anywhere in this system" —
// true when Phase 10 wrote it, false since Task 1 built the outbox schema and
// Task 5 wired a transport. That is the SAME defect class
// `routes/notifications.ts`'s own header describes for its server-side
// notice, one layer up: a claim nobody remembered to keep true. The banner is
// now DERIVED from `notificationDelivery` — never written down as a constant
// — and it distinguishes THREE states rather than one, because `SIMULATED` is
// a distinct terminal status from `SENT` on purpose (`054_notification_delivery.sql`):
// a reader must never have to know which transport was configured to know
// whether mail actually left the building.
import { plainCode, plainProse } from '../lib/plain-words'
import { CodeBadge, StateBadge } from '../components/StatusBadge'
import { Fragment, useEffect, useState, type FormEvent } from 'react'
import { SgdsAlert, SgdsButton, SgdsInput, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { isNotificationDelivery, readNotificationDelivery, type NotificationDelivery } from '@prs2/contracts'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useOffsetPager } from '../lib/useOffsetPager'
import { Pagination } from '../components/Pagination'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { BoundedField } from '../components/BoundedField'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { FormResult, type FormResultState } from '../components/FormResult'
import { boundsProblems } from '../lib/published-bounds'
import { targetValue } from '../lib/sgds-dom'
import { formatIsoDate, formatInstant } from '../lib/format'
import type { NotificationOutboxRunResult } from '../lib/types'

/**
 * ★★ WAVE 8 (Task 3) — ONE copy of the bounded field's label: read by
 * `boundsProblems` for its message AND by the field that renders it.
 *
 * ⚠ Only fields the SERVER publishes a bound for appear here — see
 * `routes/administration-field-bounds.ts`.
 */
const BOUNDED_FIELDS = {
  recipientList: 'Recipient list (comma-separated)',
} as const

const EVENT_TYPE = 'ECS_ENTITLEMENT_CREATED'

// ══ THE HONESTY BANNER — the whole point of this task ═══════════════════════

/**
 * What this build may say about `notificationDelivery`, narrowed from the raw
 * wire value through `readNotificationDelivery`/`isNotificationDelivery`
 * (`@prs2/contracts`) rather than trusted as the bare union.
 *
 * ⚠ `'unknown'` is NOT one of the three real capabilities — it is what a
 * response that said nothing usable, or something this build does not
 * recognise, collapses to. It is deliberately treated as the DANGEROUS case
 * everywhere below (banner AND disabled actions), the same discipline
 * `discloseSettlementDispatch`/`environmentBanner` (`lib/environment.ts`)
 * already apply to the sibling capability fields: an ambiguous safety signal
 * must never resolve to the permissive branch.
 */
type DeliveryCapability = NotificationDelivery | 'unknown'

function capabilityOf(raw: string): DeliveryCapability {
  const report = readNotificationDelivery(raw)
  return report.reported !== null && isNotificationDelivery(report.reported) ? report.reported : 'unknown'
}

interface DeliveryBanner {
  readonly variant: 'info' | 'warning' | 'danger'
  readonly title: string
  readonly body: string
}

/**
 * The three prescribed sentences, one per real capability. `live` names the
 * relay's ROLE — an outbound mail relay that genuinely delivers — and never
 * its address, host or credential: this is an officer-facing screen, not an
 * operations log.
 *
 * ⚠ `satisfies Record<NotificationDelivery, DeliveryBanner>` makes a fourth
 * capability value fail to COMPILE here, the same discipline
 * `routes/notifications.ts`'s own `DELIVERY_NOTICES` uses server-side —
 * so a widened union cannot silently inherit an old sentence.
 */
const DELIVERY_BANNERS = {
  none: {
    variant: 'warning',
    title: 'No mail transport is configured',
    body: 'No mail transport is configured in this deployment; nothing will be sent.',
  },
  simulated: {
    variant: 'info',
    title: 'Simulated transport: nothing leaves the building',
    body: 'A SIMULATED transport is configured. Messages are recorded, not delivered.',
  },
  live: {
    variant: 'warning',
    title: 'Live mail relay configured',
    body:
      'A LIVE outbound mail relay is configured in this deployment. A delivery that reaches SENT was accepted by ' +
      'a real mail relay for a real recipient.',
  },
} satisfies Record<NotificationDelivery, DeliveryBanner>

/** The dangerous, ambiguous case — never the reassuring default. See `DeliveryCapability`'s own note. */
const UNKNOWN_DELIVERY_BANNER: DeliveryBanner = {
  variant: 'danger',
  title: 'This build cannot confirm which mail transport is configured',
  body:
    'The gateway answered without a value this build recognises for notificationDelivery, so it cannot tell you ' +
    'whether anything sent from here can reach a real recipient: assume it can.',
}

function bannerFor(capability: DeliveryCapability): DeliveryBanner {
  return capability === 'unknown' ? UNKNOWN_DELIVERY_BANNER : DELIVERY_BANNERS[capability]
}

/**
 * Why Run outbox / Requeue are disabled, or `null` when they are not.
 *
 * ⚠ Only `live`/`simulated` enable either action — `none` (nothing to send)
 * AND `unknown` (an ambiguous signal) are both refused, with a STATED reason
 * rather than a silently-disabled control. "A disabled control with no
 * explanation is the defect this wave keeps finding in prose form" (Task 7
 * brief) — so every branch below returns a sentence, never bare `null` for
 * an unsafe state.
 */
function actionsDisabledReason(capability: DeliveryCapability | null): string | null {
  switch (capability) {
    case null:
      // Covers BOTH "still loading" and "the deliveries request failed" — the
      // ErrorCard above the table names the failure in the latter case; this
      // reason only has to justify why the action is not offered, which is the
      // same justification either way: nothing has confirmed a safe transport.
      return 'This build has not confirmed which mail transport is configured.'
    case 'none':
      return 'No mail transport is configured in this deployment; nothing would be sent.'
    case 'unknown':
      return 'This build could not confirm which mail transport is configured: treated as unsafe until it can.'
    case 'live':
    case 'simulated':
      return null
  }
}

// ══ THE DELIVERY QUEUE ═══════════════════════════════════════════════════

type DeliverySortKey =
  | 'id'
  | 'eventType'
  | 'recipient'
  | 'subject'
  | 'status'
  | 'attempts'
  | 'nextAttemptAt'
  | 'lastError'

const DELIVERY_COLUMN_LABELS: Readonly<Record<DeliverySortKey, string>> = {
  id: 'Id',
  eventType: 'Event',
  recipient: 'Recipient',
  subject: 'Subject',
  status: 'Status',
  attempts: 'Attempts',
  nextAttemptAt: 'Next attempt',
  lastError: 'Last error',
}


// ══ THE NOTIFICATION LOG ══════════════════════════════════════════════════
//
// ★★★ ADDED 2026-08-24 BY THE SORTABLE-HEADER SWEEP. Six plain `<th>`s over an
// offset-paged list that gains a row per notifiable bill event — P2C1 §15 puts the
// daily bill volume at 4,879 — so a client sort would have ordered one page of a
// table that is thousands of rows behind by the second day.
//
// ⚠ Sorted on the SERVER; see `NOTIFICATION_LOG_SORT_COLUMNS` for the whitelist,
// for which three columns are nullable, and for why `status` is RANKED rather than
// compared as text.

type LogSortKey = 'eventType' | 'resourceId' | 'recipientsSnapshot' | 'status' | 'note' | 'triggeredAt'

const LOG_COLUMN_LABELS: Readonly<Record<LogSortKey, string>> = {
  eventType: 'Event',
  resourceId: 'Officer',
  recipientsSnapshot: 'Recipients (at the time)',
  status: 'Status',
  note: 'Why',
  triggeredAt: 'Triggered at',
}

const ALL_STATUSES = 'ALL'

type RunOutboxState =
  | { readonly status: 'idle' }
  | { readonly status: 'running' }
  | { readonly status: 'done'; readonly result: NotificationOutboxRunResult; readonly transportId: string }
  | { readonly status: 'error'; readonly message: string }

type RequeueState = { readonly status: 'idle' } | { readonly status: 'requeuing' } | { readonly status: 'error'; readonly message: string }

/** The attempt ledger for ONE delivery, and — for an ABANDONED row only — the way back into the queue. */
function AttemptsPanel({
  deliveryId,
  disabledReason,
  onRequeued,
}: {
  readonly deliveryId: string
  /** `null` when Requeue may run; otherwise the reason it is refused. Same value the page computed for Run outbox. */
  readonly disabledReason: string | null
  /** Called after a successful requeue, so the outer table can reload and show the row's new status. */
  readonly onRequeued: () => void
}) {
  const attempts = useApi(() => api.notificationDeliveryAttempts(deliveryId), [deliveryId])
  const [requeueState, setRequeueState] = useState<RequeueState>({ status: 'idle' })
  const [requeueMessage, setRequeueMessage] = useState<string | null>(null)
  /**
   * ★★ WAVE 26 — Requeue FREEZES a sentence and asks; the write happens only from
   * the confirmation (`ConfirmWrite`'s header says why every irreversible write
   * does this now). Re-arming an ABANDONED row puts it back in front of the next
   * outbox run, and on a live transport that run mails a real recipient.
   */
  const [confirmingRequeue, setConfirmingRequeue] = useState<string | null>(null)

  const requestRequeue = () => {
    const recipient = attempts.data?.delivery.recipient ?? 'its recipient'
    setConfirmingRequeue(
      `Requeue delivery ${deliveryId} to ${recipient}. It is re-armed with a fresh retry budget and attempted on the next outbox run; on a live transport that run mails it. There is no undo. A mistake is corrected by another record.`,
    )
  }

  const handleRequeue = async () => {
    setConfirmingRequeue(null)
    setRequeueState({ status: 'requeuing' })
    setRequeueMessage(null)
    try {
      await api.requeueNotificationDelivery(deliveryId)
      setRequeueState({ status: 'idle' })
      setRequeueMessage('Requeued. It will be attempted on the next outbox run, with a fresh retry budget.')
      attempts.reload()
      onRequeued()
    } catch (error) {
      setRequeueState({
        status: 'error',
        message: error instanceof ApiError ? error.message : 'Could not requeue this delivery.',
      })
    }
  }

  return (
    <div className="pt-well p-4">
      {attempts.error !== null ? (
        <ErrorCard title="Could not load the attempt ledger" message={attempts.error} onRetry={attempts.reload} />
      ) : attempts.initialLoading ? (
        <LoadingCard label="Loading the attempt ledger" rows={2} />
      ) : (
        <>
          <p className="text-sm font-semibold mb-1">
            Attempt ledger, {attempts.data?.delivery.recipient} ({attempts.data?.delivery.status})
          </p>
          <p className="text-xs text-[var(--sgds-body-color-subtle)] mb-3 max-w-[70ch]">
            Append-only and ordered as attempted, never re-sorted, because the order IS the content. This delivery's
            current retry budget is {attempts.data?.delivery.attempts}, of {attempts.data?.delivery.attemptCount} attempt
            {attempts.data?.delivery.attemptCount === 1 ? '' : 's'} ever recorded in this ledger.
          </p>
          {attempts.data?.attempts.length === 0 ? (
            <EmptyCard
              title="No attempts recorded"
              message="Nothing has been attempted for this delivery yet: it is still queued, or no transport was configured the last time it was picked up."
              goodNews
            />
          ) : (
            // ⚠ Deliberately NOT a `SortableTh` table — the one exception to
            // "tables are sortable by default" on this page, and for a stated
            // reason rather than an oversight: this ledger is append-only
            // (the same `audit.refuse_mutation()` discipline `audit.event`
            // carries) and its order IS its content — "what did we do, in
            // order". Re-sorting it by outcome or SMTP code would present a
            // SEQUENCE as a SET, exactly as sorting a bill's transition
            // ledger by status would. See `listNotificationDeliveryAttempts`'s
            // own header (`notification-delivery-repository.ts`) for the same
            // rule stated from the server side.
            <div className="pt-table-wrap">
              <table className="pt-table">
                <caption className="sr-only">Every attempt recorded for this delivery, in the order it was made</caption>
                <thead>
                  <tr>
                    <th scope="col" className="pt-num" data-unsortable="the attempt sequence itself — an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set">
                      #
                    </th>
                    <th scope="col" data-unsortable="an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set">Transport</th>
                    <th scope="col" data-unsortable="an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set">Outcome</th>
                    <th scope="col" className="pt-num" data-unsortable="an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set">
                      SMTP code
                    </th>
                    <th scope="col" data-unsortable="an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set">Detail</th>
                    <th scope="col" data-unsortable="an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set">Started</th>
                    <th scope="col" data-unsortable="an append-only ledger whose READING ORDER is its content — re-sorting it would present a sequence as a set">Finished</th>
                  </tr>
                </thead>
                <tbody>
                  {attempts.data?.attempts.map((a) => (
                    <tr key={a.attemptNo}>
                      <td className="pt-num pt-tabular">{a.attemptNo}</td>
                      <td className="pt-mono">{a.transportId}</td>
                      <td>
                        <StateBadge value={a.outcome} />
                      </td>
                      <td className="pt-num pt-tabular">{a.smtpCode ?? 'None'}</td>
                      <td className="text-sm max-w-[48ch]">{plainProse(a.detail)}</td>
                      <td className="pt-tabular text-sm">{formatInstant(a.startedAt)}</td>
                      <td className="pt-tabular text-sm">{formatInstant(a.finishedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {attempts.data?.delivery.status === 'ABANDONED' && (
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <SgdsButton
                variant="outline"
                tone="brand"
                type="button"
                disabled={disabledReason !== null || requeueState.status === 'requeuing' || confirmingRequeue !== null}
                loading={requeueState.status === 'requeuing'}
                title={disabledReason ?? undefined}
                onClick={requestRequeue}
              >
                Requeue this delivery
              </SgdsButton>
              {disabledReason !== null && (
                <span className="text-sm text-[var(--sgds-body-color-subtle)]">{disabledReason}</span>
              )}
            </div>
          )}
          {confirmingRequeue !== null && (
            <div className="mt-3">
              <ConfirmWrite
                summary={confirmingRequeue}
                confirmLabel="Yes, requeue it"
                onConfirm={() => void handleRequeue()}
                onCancel={() => setConfirmingRequeue(null)}
              />
            </div>
          )}
          {requeueState.status === 'error' && (
            <p className="text-sm text-[var(--sgds-danger-color-emphasis)] mt-2 max-w-[70ch]">{plainProse(requeueState.message)}</p>
          )}
          {requeueMessage !== null && requeueState.status === 'idle' && (
            <p className="text-sm text-[var(--sgds-success-color-emphasis)] mt-2">{requeueMessage}</p>
          )}
        </>
      )}
    </div>
  )
}

/**
 * ⚠ TASK 7 REVIEW, IMPORTANT 2. The row-expand toggle is the SOLE path to
 * both the attempt ledger and the Requeue action, and until this fix it
 * carried no `aria-expanded`/`aria-controls` — a screen-reader user got only
 * a changing text label ("View attempts" / "Hide attempts"), which requires
 * re-reading rather than being announced as a state change. Follows the
 * SAME convention `HelpPage.tsx`'s topic cards and `ServiceDatePicker.tsx`'s
 * calendar trigger already use, including the panel only existing in the DOM
 * while expanded — `ServiceDatePicker`'s own `popoverId` target is
 * conditionally rendered exactly the same way.
 */
function attemptsPanelId(deliveryId: string): string {
  return `notification-delivery-attempts-${deliveryId}`
}

/**
 * Rows per page on each of this screen's two tables. Both sit under their own
 * route's `MAX_*_LIMIT` at the gateway (100 for the log, 30 for the outbox), so
 * the server never clamps what the screen asked for — a clamped request would
 * show fewer rows than the pager's arithmetic assumes and put the count out by
 * one. ⚠ The outbox's is 30 because that IS its cap: asking for more would be
 * asking the server to refuse and silently give 30 anyway.
 */
const LOG_PAGE_SIZE = 25
const DELIVERY_PAGE_SIZE = 30

export function NotificationsPage() {
  const rules = useApi(() => api.notificationRules(), [])
  /**
   * ★★ THE LOG'S OWN PAGE — 2026-08-18. One row per notifiable bill event
   * against a cap of 100, and P2C1 §15 puts the daily bill volume at 4,879: this
   * surface could fall two days behind on its first day, and nothing on this
   * screen could reach past the first hundred rows.
   */
  const [logOffset, setLogOffset] = useState(0)
  const logSort = useSort<LogSortKey>()
  const log = useApi(
    () => api.notificationLog(LOG_PAGE_SIZE, logOffset, sortParams(logSort.sort)),
    [logOffset, logSort.sort.column, logSort.sort.dir],
  )
  const logPager = useOffsetPager(LOG_PAGE_SIZE, log.data?.total)
  useEffect(() => {
    if (logPager.offset !== logOffset) setLogOffset(logPager.offset)
  }, [logPager.offset, logOffset])
  /*
    ⚠ A SORT CHANGE RESTARTS THIS WALK, and only this one. Page three of one order
    is not page three of another, so leaving the offset alone would land the reader
    in the middle of a list they have not seen the top of — and re-ordering the LOG
    must not throw the OUTBOX walk beside it back to page one, which is why the two
    tables have separate handlers rather than one shared reset.
  */
  const onLogSort = (column: LogSortKey) => {
    logSort.onSort(column)
    logPager.reset()
    setLogOffset(0)
  }

  // ⚠ TASK 8's DOCUMENTS-PASS FINDING. Until this fix this screen authored a
  // rule for `EVENT_TYPE` (`ECS_ENTITLEMENT_CREATED`) ONLY, so Annex A 2-4's
  // three bill events (`BILL_SUBMITTED`, `BILL_ADJUSTED_UPWARD`,
  // `BILL_ADJUSTED_DOWNWARD`) — which the server already accepts, and which
  // `GET /api/notifications/rules` already lists in `eventTypes` — were
  // configurable only by calling the route directly. `selectedEventType`
  // defaults to `EVENT_TYPE` so every existing behaviour (heading, form
  // submission) is unchanged until an operator actively picks something else.
  const [selectedEventType, setSelectedEventType] = useState(EVENT_TYPE)
  const [recipientList, setRecipientList] = useState('')
  const [templateCode, setTemplateCode] = useState('')
  const [enabled, setEnabled] = useState('true')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  /**
   * ★★ WAVE 8 (Task 3) REVIEW FINDING — THE SAVE OUTCOME IS ANNOUNCED, like every
   * other administration write form's.
   *
   * ⚠ Until this fix the rule form reported itself through a bare
   * `<p className="text-sm">{…}</p>`: **nothing about a paragraph appearing in the
   * DOM is announced.** The officer pressed Save rule, focus stayed on the button,
   * and the sentence saying whether the rule was stored or refused was painted
   * where they were not looking — the WCAG 2.2 3.3.1 gap
   * `components/FormResult.tsx` exists to close, and which the other twelve
   * catalogue forms already close.
   *
   * ⚠ It stopped being defensible as a "pre-existing Wave 5 gap" the moment Task 3
   * replaced this form's recipient-list field, added a `problems.any` gate and
   * edited its submit button: a screen-reader user got the NEW field-level errors
   * announced and the save outcome still silent, inside the same `<form>`.
   *
   * ⚠ `FormResult` chooses the politeness per outcome — a refusal INTERRUPTS
   * (`role="alert"`), a success waits its turn (`role="status"`) — which is the
   * decision its header exists to make once rather than on thirteen screens.
   */
  const [ruleForm, setRuleForm] = useState<FormResultState>({ status: 'idle' })
  /**
   * ★★ WAVE 26 — Save rule FREEZES a sentence and asks; the write happens only
   * from the confirmation (`ConfirmWrite`'s header says why every irreversible
   * write does this now). The sentence is composed once, from the fields as they
   * were when Save rule was pressed — never re-derived from a form the officer
   * may since have edited.
   */
  const [confirmingRule, setConfirmingRule] = useState<string | null>(null)

  const [officerId, setOfficerId] = useState('')
  /**
   * ★★ WAVE 8 (Task 3) THIRD-ROUND REVIEW FINDING — THE eCS TRIGGER'S OUTCOME IS
   * ANNOUNCED TOO, because a screen cannot be half-accessible.
   *
   * ⚠ This was `triggerMessage: string | null`, rendered as
   * `<p className="text-sm mt-3">` — the SAME bare paragraph the rule form forty
   * lines above it stopped using in the previous pass. The result was a screen that
   * was internally inconsistent: one `<form>` announced its outcome through
   * `FormResult` and the other, on the same page, painted it where nobody is
   * looking. WCAG 2.2 3.3.1 does not care which form an officer used.
   *
   * ⚠ And the previous pass's own evidence read as though it did not: a
   * `bareParagraphs: 0` probe scoped to the rule form was reported as a
   * page-level claim. There is now no bare outcome paragraph on this screen at all,
   * which is what that number actually asserts.
   *
   * ⚠ The 201 always carries `notice`; a 403 (this action is reserved to PSD) and a
   * 400 arrive as an `ApiError`, and `FormResult` puts those in `role="alert"` so a
   * refusal INTERRUPTS. That politeness split is the whole reason this is a
   * component and not an attribute — see `components/FormResult.tsx`.
   */
  const [triggerForm, setTriggerForm] = useState<FormResultState>({ status: 'idle' })
  /**
   * ★★ WAVE 26 — Record event asks too. It is the seam a real eCS integration
   * would call, and every press writes a notifiable event that queues a
   * notification to whoever the rule in force names; nothing here un-records it.
   */
  const [confirmingTrigger, setConfirmingTrigger] = useState<string | null>(null)

  // ── The delivery queue (Task 7) ──────────────────────────────────────────
  const { sort, onSort, interacted } = useSort<DeliverySortKey>()
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES)
  /**
   * ★★ THE OUTBOX PAGE — 2026-08-18. This table carries ONE ROW PER RECIPIENT
   * per event (050/054 chose that granularity so a bounce is a fact about one
   * address) against a cap of 30 — the worst ratio of any list surface in the
   * product, and until now the officer could see thirty of them and no more.
   */
  const [deliveryOffset, setDeliveryOffset] = useState(0)
  const deliveries = useApi(
    () =>
      api.notificationDeliveries({
        ...sortParams(sort),
        ...(statusFilter !== ALL_STATUSES && { status: statusFilter }),
        offset: deliveryOffset,
      }),
    [sort.column, sort.dir, statusFilter, deliveryOffset],
  )
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [runState, setRunState] = useState<RunOutboxState>({ status: 'idle' })
  /**
   * ★★ **CONFIRM BEFORE A RUN THAT ACTUALLY SENDS, and only then.**
   *
   * `docs/delivery/DEMO-READINESS-PLAN.md` §Wave 5 records confirmation as
   * inconsistent across this product and names this control specifically:
   * "'Run outbox' — which against a live transport sends real mail — has none".
   * It was one click, and against `live` that click is IRREVERSIBLE and
   * OUTWARD-FACING: mail leaves the boundary to real recipients and no part of
   * this product can recall it.
   *
   * ⚠ **`simulated` is deliberately NOT confirmed.** It records everything and
   * delivers nothing, writing SIMULATED and never SENT. Asking an operator to
   * confirm an action with no external effect is how a confirmation stops being
   * read — and the day it stops being read is the day the `live` one stops
   * working. The prompt exists because the two are different, so it must fire
   * only where they differ.
   */
  const [confirmingRun, setConfirmingRun] = useState(false)

  // The full list this deployment can notify on, read from the server — never
  // hardcoded here — with `EVENT_TYPE` as the only fallback before the first
  // response arrives, so `selectedEventType`'s initial value always matches a
  // rendered option.
  /*
    ⚠ The recipient-list ceiling comes from the PAYLOAD (`validation` on
    GET /api/notifications/rules), never from this file — see
    `lib/published-bounds.ts`. It is the one bound this form carries: `templateCode`
    and `enabled` are checked for existence and membership respectively, and
    `effectiveFrom` is a calendar-date parse, so none of the three has a length or
    syntax rule for a screen to state.
  */
  const validation = rules.data?.validation
  const problems = boundsProblems(validation, { recipientList: recipientList.trim() }, BOUNDED_FIELDS)

  const eventTypes = rules.data?.eventTypes ?? [EVENT_TYPE]
  const currentRule = rules.data?.rules.find((r) => r.eventType === selectedEventType)

  const handleEstablish = (event: FormEvent) => {
    event.preventDefault()
    if (recipientList.trim().length === 0 || effectiveFrom.length === 0 || problems.any) return
    const template = templateCode.trim().length > 0 ? templateCode.trim() : 'no template'
    setConfirmingRule(
      `Apply a ${selectedEventType} notification rule from ${effectiveFrom}: recipients ${recipientList.trim()}, ${template}, ${enabled === 'true' ? 'enabled' : 'disabled'}. Every ${selectedEventType} event raised on or after that day is notified under this rule and the one in force today is superseded. There is no undo. A mistake is corrected by another record.`,
    )
  }

  const performRuleWrite = async () => {
    setConfirmingRule(null)
    setRuleForm({ status: 'submitting' })
    try {
      await api.supersedeNotificationRule(selectedEventType, {
        effectiveFrom,
        recipientList: recipientList.trim(),
        templateCode: templateCode.trim().length > 0 ? templateCode.trim() : null,
        enabled: enabled === 'true',
      })
      setRuleForm({
        status: 'success',
        message: `The ${selectedEventType} notification rule is saved, in force from ${effectiveFrom}.`,
      })
      rules.reload()
    } catch (error) {
      setRuleForm({ status: 'error', message: error instanceof Error ? error.message : 'Could not save the rule.' })
    }
  }

  const handleTrigger = (event: FormEvent) => {
    event.preventDefault()
    if (officerId.trim().length === 0) return
    setConfirmingTrigger(
      `Record an eCS entitlement-creation event for officer ${officerId.trim()}. It goes into the notification log and queues a notification to the recipients on the ${EVENT_TYPE} rule in force today. There is no undo. A mistake is corrected by another record.`,
    )
  }

  const performTriggerWrite = async () => {
    setConfirmingTrigger(null)
    setTriggerForm({ status: 'submitting' })
    try {
      const res = await api.recordEcsEntitlementEvent(officerId.trim())
      setTriggerForm({ status: 'success', message: res.notice })
      log.reload()
      deliveries.reload()
    } catch (error) {
      setTriggerForm({ status: 'error', message: error instanceof Error ? error.message : 'Could not record the event.' })
    }
  }

  const handleRunOutbox = async () => {
    setRunState({ status: 'running' })
    try {
      const res = await api.runNotificationOutbox()
      setRunState({ status: 'done', result: res.result, transportId: res.transportId })
    } catch (error) {
      setRunState({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not run the outbox.' })
    } finally {
      deliveries.reload()
      // ⚠ A run can move ANY row's status (QUEUED -> SENDING -> a terminal
      // one). The table row updates from the reload above; an already-OPEN
      // attempt-ledger panel would not — it fetched once, on expansion, and
      // has no reason to refetch on its own. Collapsing it here means the
      // operator sees a re-opened panel's FRESH read rather than a stale one
      // sitting under a row whose status just changed.
      setExpandedId(null)
    }
  }

  const rows = deliveries.data?.deliveries ?? []
  const total = deliveries.data?.total ?? 0
  const deliveryPager = useOffsetPager(DELIVERY_PAGE_SIZE, total)

  useEffect(() => {
    if (deliveryPager.offset !== deliveryOffset) setDeliveryOffset(deliveryPager.offset)
  }, [deliveryPager.offset, deliveryOffset])

  // ⚠ A status filter or sort change is a DIFFERENT population — page three of
  // one is not page three of the other, and an untouched offset shows an empty
  // table that reads as "nothing queued" when there is plenty.
  useEffect(() => {
    deliveryPager.reset()
  }, [sort.column, sort.dir, statusFilter])
  const statusOptions = deliveries.data?.statuses ?? []

  // ⚠ `null` while no response has arrived yet — see `bannerFor`'s own note:
  // nothing is shown before the server has spoken, the same rule
  // `environmentBanner`'s `'unknown'` case follows.
  //
  // ⚠ TASK 7 REVIEW, IMPORTANT 1. `deliveries.error === null` is checked
  // ALONGSIDE `deliveries.data !== undefined`, not `data` alone. `useApi`
  // deliberately never clears `data` on a failed fetch (its own header: a
  // sortable table that unmounted itself on every reload used to steal focus
  // from the very header a keyboard user had just pressed), which is correct
  // for the TABLE — but this capability read used to derive from `data` only,
  // so a reload that fails AFTER an earlier one succeeded (a sort click while
  // the gateway hiccups, say) left the banner and the Run outbox/Requeue
  // enabled-state asserting the STALE pre-failure transport state, directly
  // under an `ErrorCard` correctly reporting the failure. Gating on the error
  // too collapses that back to the same "not confirmed" `null` posture a
  // first load has — the same fail-toward-disabled discipline this whole
  // page commits to, now honoured on every reload, not only the first one.
  const capability: DeliveryCapability | null =
    deliveries.data !== undefined && deliveries.error === null ? capabilityOf(deliveries.data.notificationDelivery) : null
  const banner = capability !== null ? bannerFor(capability) : null
  const disabledReason = actionsDisabledReason(capability)

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">eCS entitlement notifications</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          Configure who would be notified when a new eCS entitlement is created, record that events
          of this kind occurred, and see what became of every message the outbox below queued.
        </p>
      </div>

      {banner !== null && (
        <SgdsAlert show variant={banner.variant} title={banner.title}>
          {banner.body}
          {deliveries.data !== undefined && ` ${deliveries.data.notice}`}
        </SgdsAlert>
      )}

      <div className="pt-card">
        <h2 className="text-lg mb-4">Notification rule: {plainCode(selectedEventType)}</h2>
        {rules.error !== null ? (
          <ErrorCard title="Could not load the rule" message={rules.error} onRetry={rules.reload} />
        ) : rules.initialLoading ? (
          <LoadingCard label="Loading rule" rows={2} />
        ) : (
          <>
            <div className="max-w-xs mb-4">
              <SgdsSelect
                label="Event type"
                hintText="Every event type this deployment can notify on, every event this deployment can notify on, including the three bill events."
                value={selectedEventType}
                onSgdsChange={(e) => {
                  setSelectedEventType(targetValue(e))
                  // ⚠ Back to `idle`, so a live region does not keep announcing an
                  // outcome that belonged to a DIFFERENT event type's rule — and a
                  // pending confirmation is dropped for the same reason: its frozen
                  // sentence names the event type that was selected when Save was
                  // pressed, and confirming it now would write under the old one.
                  setRuleForm({ status: 'idle' })
                  setConfirmingRule(null)
                }}
              >
                {eventTypes.map((et) => (
                  <SgdsSelectOption key={et} value={et}>
                    {plainCode(et)}
                  </SgdsSelectOption>
                ))}
              </SgdsSelect>
            </div>
            {currentRule !== undefined ? (
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 mb-4 max-w-lg text-sm">
                <dt className="text-[var(--sgds-body-color-subtle)]">Recipients</dt>
                <dd>{currentRule.recipientList}</dd>
                <dt className="text-[var(--sgds-body-color-subtle)]">Template</dt>
                <dd>{currentRule.templateCode ?? 'None'}</dd>
                <dt className="text-[var(--sgds-body-color-subtle)]">Enabled</dt>
                <dd>{currentRule.enabled ? 'Yes' : 'No'}</dd>
                <dt className="text-[var(--sgds-body-color-subtle)]">In force from</dt>
                <dd>{formatIsoDate(currentRule.effective.from)}</dd>
              </dl>
            ) : (
              <EmptyCard title="No rule in force" message="No notification rule is currently configured for this event type." />
            )}

            <form className="flex flex-col gap-4 max-w-lg" onSubmit={handleEstablish}>
              <BoundedField
                label={BOUNDED_FIELDS.recipientList}
                example="e.g. moh-hr@agency.gov.sg.invalid."
                value={recipientList}
                onValueChange={setRecipientList}
                bounds={validation?.recipientList}
                problem={problems.of.recipientList}
                required
              />
              <SgdsInput
                label="Template code (optional)"
                value={templateCode}
                onSgdsInput={(e) => setTemplateCode(targetValue(e))}
                placeholder="e.g. ECS_NOTICE"
              />
              <SgdsSelect label="Enabled" value={enabled} onSgdsChange={(e) => setEnabled((e.target as HTMLSelectElement).value)}>
                <SgdsSelectOption value="true">Yes</SgdsSelectOption>
                <SgdsSelectOption value="false">No</SgdsSelectOption>
              </SgdsSelect>
              <SgdsInput
                label="Effective from (YYYY-MM-DD)"
                value={effectiveFrom}
                onSgdsInput={(e) => setEffectiveFrom(targetValue(e))}
                placeholder="e.g. 2026-08-15"
              />
              <div>
                <SgdsButton
                  type="submit"
                  variant="primary"
                  disabled={
                    recipientList.trim().length === 0 || effectiveFrom.length === 0 || problems.any || confirmingRule !== null
                  }
                  loading={ruleForm.status === 'submitting'}
                >
                  Save rule
                </SgdsButton>
              </div>
              {confirmingRule !== null && (
                <ConfirmWrite
                  summary={confirmingRule}
                  confirmLabel="Yes, apply it"
                  onConfirm={() => void performRuleWrite()}
                  onCancel={() => setConfirmingRule(null)}
                />
              )}
              <FormResult state={ruleForm} />
            </form>
          </>
        )}
      </div>

      <div className="pt-card">
        <h2 className="text-lg mb-4">Record an eCS entitlement-creation event</h2>
        <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch] mb-4">
          No eCS system is integrated with PRS2 today, so there is no live upstream event to trigger this
          automatically. This is the seam a real integration would call: exercised here directly.
        </p>
        <form className="flex items-end gap-3 flex-wrap" onSubmit={handleTrigger}>
          <SgdsInput label="Officer ID" value={officerId} onSgdsInput={(e) => setOfficerId(targetValue(e))} placeholder="e.g. OFF-02" />
          <SgdsButton
            type="submit"
            variant="primary"
            disabled={officerId.trim().length === 0 || confirmingTrigger !== null}
            loading={triggerForm.status === 'submitting'}
          >
            Record event
          </SgdsButton>
        </form>
        {confirmingTrigger !== null && (
          <div className="mt-3">
            <ConfirmWrite
              summary={confirmingTrigger}
              confirmLabel="Yes, record it"
              onConfirm={() => void performTriggerWrite()}
              onCancel={() => setConfirmingTrigger(null)}
            />
          </div>
        )}
        <FormResult state={triggerForm} className="mt-3" />
      </div>

      <div className="pt-card">
        <div className="flex items-start justify-between gap-4 flex-wrap pt-no-print mb-4">
          <div>
            <h2 className="text-lg mb-1">Notification delivery: the outbox</h2>
            {/*
              ⚠★ THIS SENTENCE SAID "every delivery THIS EVENT TYPE has queued",
              over a table filtered by `status` and nothing else. True when only
              Annex A story 50 raised events; false since Task 6 put three bill
              events in the same table. The list is now honest about its scope AND
              carries the Event column that makes the scope readable — an operator
              triaging a BOUNCED row has to know which of four events it belongs to
              before the requeue refusal's "correct the recipient list on the
              notification rule" means anything.
            */}
            <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
              Every delivery <strong>the outbox holds, across every event type</strong>: one row per recipient,
              with the event it came from, its status, attempts, next attempt time and last error. This table is not
              scoped to the event configured above; sort by Event to group it. There is no timer: nothing is
              attempted until an operator asks for a run.
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <SgdsButton
              type="button"
              variant="primary"
              disabled={disabledReason !== null || runState.status === 'running'}
              loading={runState.status === 'running'}
              title={disabledReason ?? undefined}
              onClick={() => {
                // A live transport sends real mail to real recipients and
                // nothing here can recall it; a simulated one delivers nothing.
                // See `confirmingRun`'s own note on why only the first is asked.
                if (capability === 'live') setConfirmingRun(true)
                else void handleRunOutbox()
              }}
            >
              Run outbox
            </SgdsButton>
            {disabledReason !== null && (
              <p className="text-xs text-[var(--sgds-body-color-subtle)] max-w-[36ch] text-right">{disabledReason}</p>
            )}
            {confirmingRun && (
              <div
                className="pt-well p-3 text-sm max-w-[46ch] text-left"
                role="alertdialog"
                aria-label="Confirm a live outbox run"
              >
                <p className="m-0 mb-2">
                  <strong>This deployment&rsquo;s transport is live.</strong> Running the outbox sends real email to
                  the recipients on each queued delivery&rsquo;s notification rule. Nothing in PRS can recall a
                  message once the relay has accepted it.
                </p>
                <div className="flex gap-2">
                  <SgdsButton
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setConfirmingRun(false)
                      void handleRunOutbox()
                    }}
                  >
                    Yes, send now
                  </SgdsButton>
                  <SgdsButton type="button" variant="outline" size="sm" onClick={() => setConfirmingRun(false)}>
                    Cancel
                  </SgdsButton>
                </div>
              </div>
            )}
          </div>
        </div>

        {runState.status === 'done' && (
          <div className="pt-well p-3 mb-4 text-sm" role="status">
            Ran against transport &ldquo;{runState.transportId}&rdquo;: claimed {runState.result.claimed}, sent{' '}
            {runState.result.sent}, simulated {runState.result.simulated}, bounced {runState.result.bounced}, retrying{' '}
            {runState.result.retrying}, abandoned {runState.result.abandoned}, not configured {runState.result.notConfigured}.
          </div>
        )}
        {runState.status === 'error' && (
          <p className="text-sm text-[var(--sgds-danger-color-emphasis)] mb-4 max-w-[70ch]">{plainProse(runState.message)}</p>
        )}

        <div className="pt-controlbar mb-4">
          <SgdsSelect
            label="Status"
            hintText="Re-queries the server; this is a genuine filter, not a client-side narrowing of one page."
            value={statusFilter}
            onSgdsChange={(e) => setStatusFilter(targetValue(e))}
          >
            <SgdsSelectOption value={ALL_STATUSES}>All statuses</SgdsSelectOption>
            {statusOptions.map((s) => (
              <SgdsSelectOption key={s} value={s}>
                {plainCode(s)}
              </SgdsSelectOption>
            ))}
          </SgdsSelect>
        </div>

        <SortAnnouncement
          sort={sort}
          interacted={interacted}
          busy={deliveries.loading}
          columnLabels={DELIVERY_COLUMN_LABELS}
          rowCount={rows.length}
        />

        {deliveries.error !== null ? (
          <ErrorCard title="Could not load the delivery queue" message={deliveries.error} onRetry={deliveries.reload} />
        ) : deliveries.initialLoading ? (
          <LoadingCard label="Loading the delivery queue" rows={4} />
        ) : rows.length === 0 ? (
          <EmptyCard
            title="Nothing is queued"
            message={
              statusFilter === ALL_STATUSES
                ? 'Nothing has been queued for ANY event type yet: this table is not scoped to the event configured above. The outbox is built and working; there is simply nothing in it right now. Use "Record an eCS entitlement-creation event" above to queue one.'
                : `No delivery currently reads ${plainCode(statusFilter)}. Try "All statuses", or a different filter.`
            }
          />
        ) : (
          <div className="pt-card p-0 overflow-hidden">
            <div className="pt-table-wrap" aria-busy={deliveries.loading}>
              <table className="pt-table">
                <caption className="sr-only">
                  Every delivery the outbox holds, across every event type: one row per recipient
                </caption>
                <thead>
                  <tr>
                    <SortableTh label="Id" sortKey="id" sort={sort} onSort={onSort} />
                    <SortableTh label="Event" sortKey="eventType" sort={sort} onSort={onSort} />
                    <SortableTh label="Recipient" sortKey="recipient" sort={sort} onSort={onSort} />
                    <SortableTh label="Subject" sortKey="subject" sort={sort} onSort={onSort} />
                    <SortableTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
                    <SortableTh label="Attempts" sortKey="attempts" sort={sort} onSort={onSort} align="right" />
                    <SortableTh label="Next attempt" sortKey="nextAttemptAt" sort={sort} onSort={onSort} />
                    <SortableTh label={DELIVERY_COLUMN_LABELS.lastError} sortKey="lastError" sort={sort} onSort={onSort} />
                    <th scope="col" className="pt-no-print" data-unsortable="one control per row, not data — a sortable header over buttons would claim to order something with no order">
                      <span className="sr-only">Attempt ledger</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <Fragment key={row.id}>
                      <tr>
                        <td className="pt-mono">{row.id}</td>
                        <td className="text-sm"><CodeBadge value={row.eventType} /></td>
                        <td className="text-sm">{row.recipient}</td>
                        <td className="text-sm max-w-[28ch]">{row.subject}</td>
                        <td>
                          <StateBadge value={row.status} />
                        </td>
                        <td className="pt-num pt-tabular" title={row.attempts !== row.attemptCount ? `${row.attemptCount} attempt(s) ever recorded in the ledger` : undefined}>
                          {row.attempts}
                        </td>
                        <td className="text-sm pt-tabular">{formatInstant(row.nextAttemptAt)}</td>
                        <td className="text-sm max-w-[32ch]">{row.lastError ?? 'None'}</td>
                        <td className="pt-no-print">
                          <SgdsButton
                            variant="ghost"
                            size="sm"
                            type="button"
                            aria-expanded={expandedId === row.id}
                            aria-controls={attemptsPanelId(row.id)}
                            onClick={() => setExpandedId((current) => (current === row.id ? null : row.id))}
                          >
                            {expandedId === row.id ? 'Hide attempts' : 'View attempts'}
                          </SgdsButton>
                        </td>
                      </tr>
                      {expandedId === row.id && (
                        <tr>
                          <td colSpan={9} className="p-0" id={attemptsPanelId(row.id)}>
                            <AttemptsPanel
                              deliveryId={row.id}
                              disabledReason={disabledReason}
                              onRequeued={() => deliveries.reload()}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-[var(--sgds-color-subtle)] px-5 py-3 border-t border-[var(--sgds-border-color-muted)] pt-no-print">
              Showing {rows.length} of {total} deliver{total === 1 ? 'y' : 'ies'}
              {statusFilter !== ALL_STATUSES ? ` reading ${plainCode(statusFilter)}` : ''}.{' '}
              {sort.column === null ? 'The server default order.' : `Sorted by ${DELIVERY_COLUMN_LABELS[sort.column]} (${sort.dir}).`}
              {/*
                ⚠ THIS SENTENCE USED TO READ: "Not every delivery in scope is
                shown — narrow the status filter to find the rest." It was
                honest when written and became FALSE the moment this table
                gained a pager: there is now another way through, and telling an
                officer to narrow a filter to reach rows they can simply page to
                is a stale honesty notice — a claim the code contradicts.
              */}
            </p>
            <Pagination pager={deliveryPager} label="deliveries" total={total} shown={rows.length} />
          </div>
        )}
      </div>

      <div className="pt-card">
        {/*
          ⚠★ Same correction as the outbox card above: `listNotificationLog`
          takes no event-type filter either, and since Task 6 the three bill
          events share this table with story 50's. The Event column below was
          already rendered — what was wrong was the prose around it claiming a
          scope the query does not apply.
        */}
        <h2 className="text-lg mb-1">Notification log: every event type</h2>
        <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch] mb-4">
          Every notifiable event recorded, newest first, whatever raised it. Not scoped to the event configured
          above; the Event column names which is which.
        </p>
        {log.error !== null ? (
          <ErrorCard title="Could not load the log" message={log.error} onRetry={log.reload} />
        ) : log.initialLoading ? (
          <LoadingCard label="Loading log" rows={3} />
        ) : (log.data?.entries.length ?? 0) === 0 ? (
          <EmptyCard title="No events recorded yet" message="Nothing has been recorded, for any event type." />
        ) : (
          // ⚠ A fragment, because the pager below is a SIBLING of the table
          // wrapper rather than inside it — a control that scrolled horizontally
          // with a wide table would be unreachable on the narrow viewports
          // P2C1 §3.3(b) requires this app to work on.
          <>
          <SortAnnouncement
            sort={logSort.sort}
            interacted={logSort.interacted}
            busy={log.loading}
            columnLabels={LOG_COLUMN_LABELS}
            rowCount={log.data?.entries.length ?? 0}
          />
          <div className="pt-table-wrap">
            <table className="pt-table">
              <caption className="sr-only">Every notifiable event recorded, across every event type, newest first</caption>
              <thead>
                <tr>
                  <SortableTh label={LOG_COLUMN_LABELS.eventType} sortKey="eventType" sort={logSort.sort} onSort={onLogSort} />
                  <SortableTh label={LOG_COLUMN_LABELS.resourceId} sortKey="resourceId" sort={logSort.sort} onSort={onLogSort} />
                  <SortableTh
                    label={LOG_COLUMN_LABELS.recipientsSnapshot}
                    sortKey="recipientsSnapshot"
                    sort={logSort.sort}
                    onSort={onLogSort}
                  />
                  <SortableTh label={LOG_COLUMN_LABELS.status} sortKey="status" sort={logSort.sort} onSort={onLogSort} />
                  {/*
                    ⚠★ SECURITY.md S-049 says the five-reason note is *"readable on
                    `NotificationsPage.tsx`"*. It was not: `entry.note` appeared
                    nowhere in this file, so a `NOT_SENT` row said only NOT_SENT and
                    an operator had no way to tell "no rule configured" from "the
                    recipient list is malformed" from "the domain is not allowed" —
                    which are answered by different people. The note is the ONLY
                    explanation the system produces, and S-049 is the finding that
                    depends on it being visible.
                  */}
                  <SortableTh label={LOG_COLUMN_LABELS.note} sortKey="note" sort={logSort.sort} onSort={onLogSort} />
                  <SortableTh label={LOG_COLUMN_LABELS.triggeredAt} sortKey="triggeredAt" sort={logSort.sort} onSort={onLogSort} />
                </tr>
              </thead>
              <tbody>
                {log.data?.entries.map((entry) => (
                  <tr key={entry.id}>
                    <td><CodeBadge value={entry.eventType} /></td>
                    <td className="pt-mono">{entry.resourceId ?? 'None'}</td>
                    <td className="text-sm">{entry.recipientsSnapshot ?? 'none configured'}</td>
                    <td>
                      <StateBadge value={entry.status} />
                    </td>
                    <td className="text-sm max-w-[40ch]">{entry.note.length > 0 ? entry.note : 'None'}</td>
                    <td className="pt-tabular">{entry.triggeredAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            pager={logPager}
            label="log entries"
            total={log.data?.total}
            shown={log.data?.entries.length ?? 0}
          />
          </>
        )}
      </div>
    </div>
  )
}
